'use client'

import { useAuthContext } from '@/hooks/use-auth-context'
import { usePathname, useSearchParams } from 'next/navigation'
import posthog from 'posthog-js'
import { PostHogProvider as Provider } from 'posthog-js/react'
import { useEffect, useRef } from 'react'

const posthogToken = process.env.NEXT_PUBLIC_POSTHOG_TOKEN
const posthogHost =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'
const posthogEnvironment =
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV
let hasInitializedPostHog = false

function isPostHogConfigured() {
  return Boolean(posthogToken)
}

function ensurePostHogInitialized() {
  if (!isPostHogConfigured() || hasInitializedPostHog) {
    return
  }

  hasInitializedPostHog = true

  posthog.init(posthogToken!, {
    api_host: posthogHost,
    defaults: '2026-01-30',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    person_profiles: 'identified_only',
    loaded: (client) => {
      client.register({
        environment: posthogEnvironment
      })
    }
  })
}

function PostHogPageViewTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previousUrl = useRef<string | null>(null)

  useEffect(() => {
    if (!isPostHogConfigured()) {
      return
    }

    ensurePostHogInitialized()

    const currentUrl = window.location.href

    posthog.capture('$pageview', {
      $current_url: currentUrl,
      $referrer: previousUrl.current ?? document.referrer
    })

    previousUrl.current = currentUrl
  }, [pathname, searchParams])

  return null
}

function PostHogIdentityTracker() {
  const { account, organization } = useAuthContext()
  const identifiedUserId = useRef<string | null>(null)

  useEffect(() => {
    if (!isPostHogConfigured()) {
      return
    }

    ensurePostHogInitialized()

    if (!account) {
      if (identifiedUserId.current) {
        posthog.reset()
        identifiedUserId.current = null
      }
      return
    }

    const userId = account.ID ?? account.Email

    if (!userId || identifiedUserId.current === userId) {
      return
    }

    posthog.identify(userId, {
      email: account.Email,
      name: [account.FirstName, account.LastName].filter(Boolean).join(' '),
      roleId: account.Role?.ID,
      roleName: account.Role?.Name,
      organizationId: account.OrganizationID,
      organizationName: organization?.Name,
      organizationType: organization?.OrganizationType
    })

    identifiedUserId.current = userId
  }, [account, organization])

  return null
}

export function PostHogAnalytics() {
  if (!isPostHogConfigured()) {
    return null
  }

  return (
    <Provider client={posthog}>
      <PostHogIdentityTracker />
      <PostHogPageViewTracker />
    </Provider>
  )
}
